import AddCommentOutlinedIcon from '@mui/icons-material/AddCommentOutlined';
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Drawer,
  Fab,
  Fade,
  IconButton,
  Paper,
  Portal,
  Slide,
  Stack,
  Typography,
} from '@mui/material';
import { buildAttachmentMarker } from '@prospero/shared/src/assistant/assistant-attachment-marker';
import type {
  AssistantAction,
  AssistantMessage,
  AssistantSendResponse,
  AssistantSurveyAuthoringContext,
} from '@prospero/shared/src/assistant/assistant-contract';
import type { KeyboardEvent as ReactKeyboardEvent, RefObject } from 'react';
import { useCallback, useContext, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '../../hooks/use-is-mobile';
import { useNotification } from '../../hooks/use-notification';
import { usePrefersReducedMotion } from '../../hooks/use-prefers-reduced-motion';
import { AssistantComposer } from './assistant-composer';
import {
  isConsultantDeadlinesPage,
  isDocGenerationDraftPage,
  isSurveyAuthoringPage,
  shouldAutoScrollToEnd,
} from './assistant-kit';
import { AssistantMark } from './assistant-mark';
import { AssistantMessageBubble, type PendingPhotoAttachment } from './assistant-message-bubble';
import { AssistantTypingIndicator } from './assistant-typing-indicator';
import type { ConsultantAssistantBlock } from './consultant-assistant-block';
import { ConsultantAssistantBlockedAlert } from './consultant-assistant-blocked-alert';
import { collectManualDraftOverlay, ManualDraftOverlayContext } from './manual-draft-overlay';
import { useAssistant } from './use-assistant';
import { useVoiceRecorder } from './voice/use-voice-recorder';

/** Tool-owned artifact of an authoring turn (schema PERSISTED server-side). */
export type SurveyAuthoringTurnResult = NonNullable<AssistantSendResponse['surveyAuthoring']>;

export interface AssistantQuickAction {
  id: string;
  label: string;
  /** Sent verbatim as the user message when the chip is tapped. */
  prompt: string;
}

export interface AssistantPanelProps {
  open: boolean;
  onClose: () => void;
  /** Current `location.pathname` of the page hosting the panel. */
  pageUrl: string;
  entryButtonRef: RefObject<HTMLButtonElement | null>;
  /** Invoked when the user taps an OPEN_GUIDE_HIT CTA. */
  onOpenGuideHit: (action: AssistantAction) => void;
  /** One-tap prompts above the composer (survey studio only). */
  quickActions?: AssistantQuickAction[];
  /** Hides the microphone (authoring turns must carry the page context). */
  voiceDisabled?: boolean;
  /**
   * Survey-authoring hook-up: when present, EVERY send (greeting included)
   * carries `surveyAuthoring` from the builder overlay, and a tool-persisted
   * schema in the response is handed to `onSurveyAuthoringResult` to be
   * applied back to the builder.
   */
  getSurveyAuthoring?: () => AssistantSurveyAuthoringContext | null;
  onSurveyAuthoringResult?: (result: SurveyAuthoringTurnResult) => void;
  /**
   * One-shot user message auto-sent when the panel opens, requested by a page
   * element that wants the assistant to start a specific task immediately —
   * today the "Gestisci con l'assistente AI" button on a non-conformity
   * section. `token` deduplicates the auto-send; the host clears the request
   * through `onAutoSendConsumed` once it has been handed to the send flow.
   */
  autoSend?: { message: string; token: number };
  onAutoSendConsumed?: () => void;
}

/** Desktop panel width; host pages use it to shift content out from under the panel. */
export const ASSISTANT_PANEL_WIDTH = 440;

/** Scroll distance (px) from the bottom past which the jump pill appears. */
const JUMP_PILL_THRESHOLD_PX = 400;

/**
 * Italian fallback of the AI Act art. 50(1) disclosure. Named and exported —
 * unlike the other `defaultValue` strings in this file — because it is the one
 * legally load-bearing sentence here: `guide-assistant-locale-parity.test.ts`
 * asserts it equals `it.json`, so the copy the user reads and the copy baked
 * into the bundle can never drift apart unnoticed.
 */
export const ASSISTANT_DISCLOSURE_FALLBACK =
  'Stai parlando con un sistema di intelligenza artificiale: le risposte sono proposte da verificare. Niente entra nel registro HACCP finché non premi il pulsante di conferma: vale sia per i documenti che finiscono in archivio sia per le temperature che dichiari qui.';

/**
 * Survey-studio variant of the AI Act art. 50(1) disclosure: on
 * /consultant/surveys/:templateId the assistant edits the questionnaire DRAFT
 * directly (applySurveySchemaEdit persists in-turn), so the generic
 * "nothing enters the register until you confirm" copy would describe a gate
 * that is not where this page's risk lies. Same legal-load-bearing status as
 * the fallback above — pinned per-locale in guide-assistant-locale-parity.test.ts.
 */
export const SURVEY_STUDIO_DISCLOSURE_FALLBACK =
  'Stai parlando con un sistema di intelligenza artificiale: le risposte sono proposte da verificare. Qui l\u2019assistente lavora solo sulla bozza del questionario e le modifiche alla struttura le applica direttamente in chat; nulla tocca il registro HACCP, che riceve dati solo dalle risposte compilate e confermate.';

/**
 * Deadline-tracker variant of the AI Act art. 50(1) disclosure: the assistant
 * drafts client deadlines and reads the tracker, and nothing is created until
 * the consultant presses "Crea". Pinned per locale in the parity test.
 */
export const CONSULTANT_DEADLINES_DISCLOSURE_FALLBACK =
  'Stai parlando con un sistema di intelligenza artificiale: le risposte sono proposte da verificare. Qui l’assistente prepara le scadenze dei tuoi clienti e legge lo scadenzario, ma non crea nulla finché non premi «Crea» sulla scheda: controlla sempre clienti, tipo di documento e date.';

type StarterGroup = 'starter' | 'starterSurvey' | 'starterManual' | 'starterConsultantDeadlines';

const STARTER_KEYS: Record<StarterGroup, readonly string[]> = {
  starter: ['one', 'two', 'three', 'four'],
  starterSurvey: ['one', 'two', 'three', 'four'],
  starterManual: ['one', 'two', 'three', 'four'],
  starterConsultantDeadlines: ['one', 'two', 'three'],
};

/** Italian fallbacks of the deadline starters until the locales ship them. */
const CONSULTANT_DEADLINES_STARTER_FALLBACKS: Record<string, string> = {
  one: 'Cosa scade questo mese?',
  two: 'Chi ha il DVR scaduto?',
  three: 'Aggiungi una scadenza a un cliente',
};

function starterGroupFor(pageUrl: string): StarterGroup {
  if (isSurveyAuthoringPage(pageUrl)) {
    return 'starterSurvey';
  }
  if (isDocGenerationDraftPage(pageUrl)) {
    return 'starterManual';
  }
  return isConsultantDeadlinesPage(pageUrl) ? 'starterConsultantDeadlines' : 'starter';
}

function AssistantDisclosure({ pageUrl }: { pageUrl: string }) {
  const { t } = useTranslation();
  let disclosure: string;
  if (isSurveyAuthoringPage(pageUrl)) {
    disclosure = t('guida.assistant.disclosureSurveyStudio', {
      defaultValue: SURVEY_STUDIO_DISCLOSURE_FALLBACK,
    });
  } else if (isConsultantDeadlinesPage(pageUrl)) {
    disclosure = t('guida.assistant.disclosureConsultantDeadlines', {
      defaultValue: CONSULTANT_DEADLINES_DISCLOSURE_FALLBACK,
    });
  } else {
    disclosure = t('guida.assistant.disclosure', { defaultValue: ASSISTANT_DISCLOSURE_FALLBACK });
  }
  return (
    <Alert icon={<AutoAwesomeOutlinedIcon fontSize="inherit" />} severity="info" sx={{ m: 2 }}>
      {disclosure}
    </Alert>
  );
}

interface SendFailureAlertProps {
  consultantAssistantBlock: ConsultantAssistantBlock | null;
  retry: () => Promise<unknown>;
  sendFailed: boolean;
}

function SendFailureAlert({ consultantAssistantBlock, retry, sendFailed }: SendFailureAlertProps) {
  const { t } = useTranslation();
  if (consultantAssistantBlock) {
    return <ConsultantAssistantBlockedAlert reason={consultantAssistantBlock} />;
  }
  if (!sendFailed) {
    return null;
  }
  return (
    <Alert
      action={
        <Button color="inherit" onClick={() => swallow(retry())} size="small">
          {t('guida.assistant.retry', { defaultValue: 'Riprova' })}
        </Button>
      }
      severity="error"
    >
      {t('guida.assistant.error', {
        defaultValue: 'Non è stato possibile contattare l’assistente.',
      })}
    </Alert>
  );
}

function swallow(operation: Promise<unknown>) {
  void operation.catch(() => undefined);
}

function optionChoiceIds(message: AssistantMessage): string[] {
  if (message.role !== 'ASSISTANT' || !message.options) {
    return [];
  }
  return message.options.choices.map((choice) => choice.id);
}

/**
 * An options block is answered once a later user message carries the marker
 * for one of its choices — derived from the thread, so it survives reloads
 * and optimistic sends alike.
 */
function collectAnsweredOptionMessageIds(messages: AssistantMessage[]): Set<string> {
  const answeredMessageIds = new Set<string>();
  const ownerMessageIdByChoiceId = new Map<string, string>();
  for (const message of messages) {
    for (const choiceId of optionChoiceIds(message)) {
      ownerMessageIdByChoiceId.set(choiceId, message.id);
    }
    if (message.role !== 'USER' || !message.text.includes('[scelta: ')) {
      continue;
    }
    for (const [choiceId, ownerMessageId] of ownerMessageIdByChoiceId) {
      if (message.text.includes(`[scelta: ${choiceId} `)) {
        answeredMessageIds.add(ownerMessageId);
      }
    }
  }
  return answeredMessageIds;
}

interface StarterChipsProps {
  hasUserMessage: boolean;
  isSending: boolean;
  isThreadLoading: boolean;
  pageUrl: string;
  sendMessage: (text: string, pageUrl: string) => Promise<unknown>;
}

function StarterChips({
  hasUserMessage,
  isSending,
  isThreadLoading,
  pageUrl,
  sendMessage,
}: StarterChipsProps) {
  const { t } = useTranslation();
  if (isThreadLoading || hasUserMessage) {
    return null;
  }
  // The authoring pages open with their own copilot's prompts: the generic
  // ones (registers, temperatures) make no sense while reviewing a manual or
  // building a survey.
  const starterGroup = starterGroupFor(pageUrl);
  const starterEntries = STARTER_KEYS[starterGroup].map((starterKey) => ({
    key: starterKey,
    text: t(`guida.assistant.${starterGroup}.${starterKey}`, {
      defaultValue:
        starterGroup === 'starterConsultantDeadlines'
          ? CONSULTANT_DEADLINES_STARTER_FALLBACKS[starterKey]
          : undefined,
    }),
  }));
  return (
    <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1 }}>
      {starterEntries.map(({ key: starterKey, text: starterText }) => (
        <Chip
          component="button"
          key={starterKey}
          disabled={isSending}
          label={starterText}
          onClick={() => swallow(sendMessage(starterText, pageUrl))}
          size="small"
          variant="outlined"
        />
      ))}
    </Stack>
  );
}

interface VoiceAlertsProps {
  recorderError: 'mic-denied' | 'recorder-unavailable' | null;
  retryVoice: () => Promise<unknown>;
  voiceFailed: { code: 'voice_empty' | 'voice_transcription_failed' } | null;
}

function VoiceAlerts({ recorderError, retryVoice, voiceFailed }: VoiceAlertsProps) {
  const { t } = useTranslation();
  return (
    <>
      {recorderError ? (
        <Alert severity="warning">
          {t(
            `guida.assistant.${recorderError === 'mic-denied' ? 'micDenied' : 'recorderUnavailable'}`,
            {
              defaultValue:
                recorderError === 'mic-denied'
                  ? 'Non riesco ad accedere al microfono. Controlla i permessi del browser e riprova.'
                  : 'Il tuo browser non supporta la registrazione vocale.',
            },
          )}
        </Alert>
      ) : null}
      {voiceFailed ? (
        <Alert
          action={
            voiceFailed.code === 'voice_transcription_failed' ? (
              <Button color="inherit" onClick={() => swallow(retryVoice())} size="small">
                {t('guida.assistant.retry', { defaultValue: 'Riprova' })}
              </Button>
            ) : undefined
          }
          severity="warning"
        >
          {t(
            `guida.assistant.${voiceFailed.code === 'voice_empty' ? 'voiceEmpty' : 'voiceFailed'}`,
            {
              defaultValue:
                voiceFailed.code === 'voice_empty'
                  ? 'Non ho capito il vocale. Prova a registrarlo di nuovo.'
                  : 'Non è stato possibile trascrivere il vocale.',
            },
          )}
        </Alert>
      ) : null}
    </>
  );
}

/**
 * Non-modal chat panel for the page-aware assistant. Desktop: positioned
 * Paper (no focus trap, no scroll lock — WAI-ARIA non-modal dialog pattern).
 * Mobile: full-screen temporary Drawer.
 */
export function AssistantPanel({
  open,
  onClose,
  pageUrl,
  entryButtonRef,
  onOpenGuideHit,
  quickActions,
  voiceDisabled = false,
  getSurveyAuthoring,
  onSurveyAuthoringResult,
  autoSend,
  onAutoSendConsumed,
}: AssistantPanelProps) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const titleId = useId();
  const newChatDialogTitleId = useId();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const listEndRef = useRef<HTMLDivElement>(null);
  const logContainerRef = useRef<HTMLDivElement | null>(null);
  const startedForOpen = useRef(false);
  const restoreEntryFocusOnExit = useRef(true);
  const messageCountRef = useRef(0);
  const [newChatConfirmOpen, setNewChatConfirmOpen] = useState(false);
  const [desktopPanelMounted, setDesktopPanelMounted] = useState(false);
  const [showJumpToBottom, setShowJumpToBottom] = useState(false);
  const { error: recorderError, isRecording, seconds, start, stop, cancel } = useVoiceRecorder();
  const reduceMotion = usePrefersReducedMotion();

  const {
    messages,
    isThreadLoading,
    isSending,
    isResetting,
    sendFailed,
    consultantAssistantBlock,
    sendMessage,
    sendVoiceMessage,
    voiceFailed,
    resetConversation,
    retry,
    retryVoice,
    uploadAttachment: uploadAttachmentMut,
    confirmAction: confirmActionMut,
  } = useAssistant({ enabled: open, pageUrl });
  const overlayBox = useContext(ManualDraftOverlayContext);
  const { showWarning } = useNotification();
  // Residual validator errors of the LAST authoring turn that persisted a
  // schema: a compact banner above the composer, cleared on the next turn.
  const [authoringIssues, setAuthoringIssues] = useState<string[]>([]);
  // The manual draft page registers its UNSAVED section editors here: every
  // typed send carries their current content so the copilot's compiled state
  // matches what the user sees, not the last saved draft.
  const sendMessageWithOverlay = useCallback(
    (text: string | null, pageUrl: string) => {
      // Manual overlay only rides TEXT turns: the auto-greeting (text null)
      // must stay the quiet system-open turn it always was.
      const { manualDraftOverlay, omittedCount } =
        text === null
          ? { manualDraftOverlay: undefined, omittedCount: 0 }
          : collectManualDraftOverlay(overlayBox);
      if (omittedCount > 0) {
        // Contract-capped overflow (too many dirty sections, or one over the
        // per-section size). The turn still goes out with what fits — the user
        // is told which edits the copilot cannot see, instead of a 400.
        showWarning(t('guida.assistant.overlayPartialContext', { count: omittedCount }));
      }
      // Authoring context: always attached when the host provides the getter —
      // `schema: null` means "the persisted DRAFT is the source of truth", and
      // its presence is what routes the WHOLE turn (greeting included) to the
      // consultant's workspace thread. The backend ignores the field off the
      // studio routes.
      const surveyAuthoring = getSurveyAuthoring ? getSurveyAuthoring() : undefined;
      return sendMessage(text, pageUrl, manualDraftOverlay, surveyAuthoring).then((response) => {
        if (getSurveyAuthoring) {
          const result = response.surveyAuthoring ?? null;
          if (result) {
            // The schema in the result is ALREADY persisted server-side by the
            // apply tool: apply it to the builder (never invalidate-refetch).
            onSurveyAuthoringResult?.(result);
          }
          setAuthoringIssues(result?.validationErrors ?? []);
        }
        return response;
      });
    },
    [overlayBox, sendMessage, showWarning, t, getSurveyAuthoring, onSurveyAuthoringResult],
  );
  // Shared by the composer's own paperclip AND the deterministic "Aggiungi
  // foto" NC button (Fase 3 side-fix) — ONE pending-attachment slot, so
  // whichever affordance staged a photo/document shows the same thumbnail +
  // removal, and its marker rides along with whatever the operator sends
  // next (typed text OR a chip tap) instead of needing its own message.
  const [attachment, setAttachment] = useState<PendingPhotoAttachment | null>(null);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  // Appends the pending attachment's marker to an options-card send (a chip
  // tap or a dropdown pick) exactly like the composer's own `submitDraft`
  // does for typed text — the ONE merge point both paths share, so a photo
  // staged under the question is never lost just because the operator
  // answered with a tap instead of typing.
  const sendOptionsTextWithAttachment = useCallback(
    (text: string) => {
      if (!attachment) {
        return sendMessageWithOverlay(text, pageUrl);
      }
      const finalText = `${text}\n${buildAttachmentMarker(attachment)}`;
      setAttachment(null);
      return sendMessageWithOverlay(finalText, pageUrl);
    },
    [attachment, pageUrl, sendMessageWithOverlay],
  );

  const hasUserMessage = messages.some((message) => message.role === 'USER');
  const answeredOptionIds = useMemo(() => collectAnsweredOptionMessageIds(messages), [messages]);

  const autoSendToken = autoSend?.token;
  const autoSendText = autoSend?.message;
  const autoSentTokenRef = useRef<number | null>(null);
  const autoSendResetStartedRef = useRef<number | null>(null);
  const [autoSendResetDoneToken, setAutoSendResetDoneToken] = useState<number | null>(null);
  const isAutoSendPending =
    autoSendToken !== undefined && autoSentTokenRef.current !== autoSendToken;

  // Resync on every open: an empty thread always opens the conversation
  // server-side (persisted greeting) instead of showing a dead end. Routed
  // through the overlay wrapper so an authoring greeting carries the
  // surveyAuthoring context and lands on the consultant workspace thread.
  // A pending host prompt skips the greeting: its own message opens the chat.
  useEffect(() => {
    if (!open) {
      startedForOpen.current = false;
      return;
    }
    if (isAutoSendPending) {
      return;
    }
    if (!isThreadLoading && !isSending && messages.length === 0 && !startedForOpen.current) {
      startedForOpen.current = true;
      swallow(sendMessageWithOverlay(null, pageUrl));
    }
  }, [
    isAutoSendPending,
    isSending,
    isThreadLoading,
    messages.length,
    open,
    pageUrl,
    sendMessageWithOverlay,
  ]);

  // One-shot prompt from a host page (e.g. the "Gestisci con l'assistente AI"
  // button on a non-conformity section). The chat is reset first: a previous
  // NC's drafts, questions and confirm cards would otherwise steer the new
  // one. Prior turns stay in the audit trail server-side. If the reset fails
  // the prompt still goes out on the existing thread rather than being lost.
  // `token` (the store nonce) makes repeated requests re-fire even when the
  // message text is identical; `onAutoSendConsumed` lets the host clear the
  // request so a later panel reopen does not replay it.
  useEffect(() => {
    if (!open || autoSendToken === undefined || !autoSendText) {
      return;
    }
    if (isThreadLoading || isSending || isResetting) {
      return;
    }
    if (autoSentTokenRef.current === autoSendToken) {
      return;
    }
    if (autoSendResetStartedRef.current !== autoSendToken) {
      autoSendResetStartedRef.current = autoSendToken;
      const markResetDone = () => setAutoSendResetDoneToken(autoSendToken);
      resetConversation().then(markResetDone, markResetDone);
      return;
    }
    if (autoSendResetDoneToken !== autoSendToken) {
      return;
    }
    autoSentTokenRef.current = autoSendToken;
    startedForOpen.current = true;
    swallow(sendMessageWithOverlay(autoSendText, pageUrl));
    onAutoSendConsumed?.();
  }, [
    autoSendResetDoneToken,
    autoSendText,
    autoSendToken,
    isResetting,
    isSending,
    isThreadLoading,
    onAutoSendConsumed,
    open,
    pageUrl,
    resetConversation,
    sendMessageWithOverlay,
  ]);

  useEffect(() => {
    if (open && !isMobile) {
      setDesktopPanelMounted(true);
    }
  }, [open, isMobile]);

  // Land on the newest message on every open and whenever the thread grows —
  // but only if the user is already reading at the bottom: a reply must never
  // yank someone away from the history they are reading.
  useEffect(() => {
    if (!open) {
      messageCountRef.current = 0;
      return;
    }
    const previousMessageCount = messageCountRef.current;
    messageCountRef.current = messages.length;
    const container = logContainerRef.current;
    const distanceFromBottom =
      container === null
        ? null
        : container.scrollHeight - container.scrollTop - container.clientHeight;
    if (
      !shouldAutoScrollToEnd({
        distanceFromBottom,
        messageCount: messages.length,
        previousMessageCount,
      })
    ) {
      return;
    }
    listEndRef.current?.scrollIntoView({ block: 'end' });
  }, [messages, open]);

  const close = useCallback(() => {
    restoreEntryFocusOnExit.current = true;
    onClose();
  }, [onClose]);

  const handleLogScroll = () => {
    const container = logContainerRef.current;
    if (container === null) {
      return;
    }
    const distanceFromBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight;
    setShowJumpToBottom(distanceFromBottom > JUMP_PILL_THRESHOLD_PX);
  };
  const jumpToLatest = () => {
    listEndRef.current?.scrollIntoView({
      behavior: reduceMotion ? 'auto' : 'smooth',
      block: 'end',
    });
    setShowJumpToBottom(false);
  };

  const handleEntered = () => {
    headingRef.current?.focus();
    listEndRef.current?.scrollIntoView({ block: 'end' });
    setShowJumpToBottom(false);
  };
  const handleExited = () => {
    if (restoreEntryFocusOnExit.current) {
      entryButtonRef.current?.focus();
    }
    restoreEntryFocusOnExit.current = true;
    setDesktopPanelMounted(false);
  };
  const handlePanelKeyDown = (event: ReactKeyboardEvent) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      close();
    }
  };

  const panelBody = (
    <Stack sx={{ height: '100%' }}>
      <Box
        sx={{
          alignItems: 'center',
          display: 'grid',
          gap: 1,
          gridTemplateColumns: '44px minmax(0, 1fr) 44px 44px',
          p: 2,
        }}
      >
        <AssistantMark />
        <Typography
          component="h2"
          id={titleId}
          ref={headingRef}
          sx={{ fontWeight: 700, minWidth: 0, outline: 'none', overflowWrap: 'anywhere' }}
          tabIndex={-1}
          variant="h6"
        >
          {t('guida.assistant.title', { defaultValue: 'Assistente AI Prospero' })}
        </Typography>
        <IconButton
          aria-label={t('guida.assistant.newChat', { defaultValue: 'Nuova chat' })}
          disabled={isResetting || messages.length === 0}
          onClick={() => setNewChatConfirmOpen(true)}
          sx={{ minHeight: 44, minWidth: 44 }}
        >
          <AddCommentOutlinedIcon />
        </IconButton>
        <IconButton
          aria-label={t('guida.assistant.close', { defaultValue: 'Chiudi assistente' })}
          onClick={close}
          sx={{ minHeight: 44, minWidth: 44 }}
        >
          <CloseRoundedIcon />
        </IconButton>
      </Box>
      <Divider />
      {/* AI Act Art. 50(1): always-visible disclosure. It must describe what the
          assistant ACTUALLY does. Since `proposeTemperatureReading` replaced the
          writing tool, NOTHING reaches the HACCP register during a turn: both
          documents and declared temperatures are born in the confirm endpoint.
          Keep this sentence in step with `confirmAction` and
          `confirmConsultantDeadlinesAction` — describing a gate that does not
          exist would be worse than no notice at all. */}
      <AssistantDisclosure pageUrl={pageUrl} />

      <Box
        sx={{
          display: 'flex',
          flex: 1,
          flexDirection: 'column',
          minHeight: 0,
          position: 'relative',
        }}
      >
        <Box
          aria-live="polite"
          onScroll={handleLogScroll}
          ref={logContainerRef}
          role="log"
          sx={{
            display: 'flex',
            flex: 1,
            flexDirection: 'column',
            gap: 1.5,
            minHeight: 0,
            overflowY: 'auto',
            p: 2,
          }}
        >
          {!isThreadLoading && messages.length === 0 ? (
            <Typography color="text.secondary" variant="body2">
              {t('guida.assistant.empty', {
                defaultValue:
                  'Chiedimi come funziona una funzione di Prospero o quale obbligo HACCP riguarda questa pagina.',
              })}
            </Typography>
          ) : null}
          {messages.map((message) => (
            <AssistantMessageBubble
              confirmAction={confirmActionMut}
              key={message.id}
              message={message}
              onOpenGuideHit={(action) => {
                // On mobile the drawer covers the destination; close it so the
                // user actually sees the topic they jumped to.
                if (isMobile) {
                  restoreEntryFocusOnExit.current = false;
                  onClose();
                }
                onOpenGuideHit(action);
              }}
              onPhotoAttachmentChange={setAttachment}
              onSendOptionsText={sendOptionsTextWithAttachment}
              optionsDisabled={answeredOptionIds.has(message.id) || isSending}
              photoAttachment={attachment}
              uploadAttachment={uploadAttachmentMut}
            />
          ))}
          <StarterChips
            hasUserMessage={hasUserMessage}
            isSending={isSending}
            isThreadLoading={isThreadLoading}
            pageUrl={pageUrl}
            sendMessage={sendMessageWithOverlay}
          />
          {isSending ? (
            <AssistantTypingIndicator
              label={t('guida.assistant.thinking', { defaultValue: 'Sto pensando…' })}
            />
          ) : null}
          <SendFailureAlert
            consultantAssistantBlock={consultantAssistantBlock}
            retry={retry}
            sendFailed={sendFailed}
          />
          <VoiceAlerts
            recorderError={recorderError}
            retryVoice={retryVoice}
            voiceFailed={voiceFailed}
          />
          <div ref={listEndRef} />
        </Box>
        <Fade in={showJumpToBottom}>
          <Fab
            aria-label={t('guida.assistant.scrollToLatest', {
              defaultValue: 'Vai all’ultimo messaggio',
            })}
            color="primary"
            onClick={jumpToLatest}
            size="small"
            sx={{ bottom: 16, position: 'absolute', right: 16 }}
          >
            <ArrowDownwardRoundedIcon />
          </Fab>
        </Fade>
      </Box>

      <Divider />
      {quickActions && quickActions.length > 0 ? (
        <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1, px: 2, pt: 1.5 }}>
          {quickActions.map((action) => (
            <Chip
              component="button"
              key={action.id}
              disabled={isSending}
              label={action.label}
              onClick={() => swallow(sendMessageWithOverlay(action.prompt, pageUrl))}
              size="small"
              variant="outlined"
            />
          ))}
        </Stack>
      ) : null}
      {getSurveyAuthoring && authoringIssues.length > 0 ? (
        <Alert icon={false} severity="warning" sx={{ mx: 2, mt: 1.5 }}>
          {t('consultant.surveyStudio.assistant.validationIssues', {
            count: authoringIssues.length,
            codes: authoringIssues.join(', '),
            defaultValue:
              'Lo schema applicato presenta {{count}} errori di validazione residui: {{codes}}',
          })}
        </Alert>
      ) : null}
      <AssistantComposer
        attachment={attachment}
        attachmentError={attachmentError}
        cancel={cancel}
        isRecording={isRecording}
        isSending={isSending}
        onAttachmentChange={setAttachment}
        onAttachmentError={setAttachmentError}
        pageUrl={pageUrl}
        seconds={seconds}
        voiceDisabled={voiceDisabled}
        sendMessage={sendMessageWithOverlay}
        sendVoiceMessage={sendVoiceMessage}
        start={start}
        stop={stop}
        uploadAttachment={uploadAttachmentMut}
      />

      <Dialog
        aria-labelledby={newChatDialogTitleId}
        onClose={() => setNewChatConfirmOpen(false)}
        open={newChatConfirmOpen}
      >
        <DialogTitle id={newChatDialogTitleId}>
          {t('guida.assistant.newChatConfirmTitle', { defaultValue: 'Iniziare una nuova chat?' })}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('guida.assistant.newChatConfirmMessage', {
              defaultValue:
                'I messaggi di questa conversazione non saranno più visibili qui. Restano comunque registrati.',
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ flexDirection: { xs: 'column', sm: 'row' }, gap: 1, p: 2 }}>
          <Button
            fullWidth={isMobile}
            onClick={() => setNewChatConfirmOpen(false)}
            sx={{ minHeight: 48 }}
          >
            {t('guida.assistant.newChatConfirmCancel', { defaultValue: 'Annulla' })}
          </Button>
          <Button
            fullWidth={isMobile}
            onClick={() =>
              swallow(
                resetConversation().then(() => {
                  startedForOpen.current = false;
                  setNewChatConfirmOpen(false);
                }),
              )
            }
            sx={{ minHeight: 48 }}
            variant="contained"
          >
            {t('guida.assistant.newChatConfirmConfirm', { defaultValue: 'Inizia nuova chat' })}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );

  return (
    <>
      {isMobile ? (
        <Drawer
          anchor="right"
          ModalProps={{ disableAutoFocus: true, disableRestoreFocus: true }}
          onClose={close}
          open={open}
          sx={{ zIndex: (theme) => theme.zIndex.drawer + 70 }}
          slotProps={{
            transition: { onEntered: handleEntered, onExited: handleExited },
            paper: {
              'aria-labelledby': titleId,
              onKeyDown: handlePanelKeyDown,
              role: 'dialog',
              sx: { maxWidth: '100vw', width: '100vw' },
            },
          }}
          variant="temporary"
        >
          {panelBody}
        </Drawer>
      ) : (
        desktopPanelMounted && (
          <Portal>
            <Slide direction="left" in={open} onEntered={handleEntered} onExited={handleExited}>
              <Paper
                aria-labelledby={titleId}
                elevation={16}
                onKeyDown={handlePanelKeyDown}
                role="dialog"
                square
                sx={{
                  bottom: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  maxWidth: '100vw',
                  position: 'fixed',
                  right: 0,
                  top: 0,
                  width: ASSISTANT_PANEL_WIDTH,
                  zIndex: (theme) => theme.zIndex.drawer + 70,
                }}
              >
                {panelBody}
              </Paper>
            </Slide>
          </Portal>
        )
      )}
    </>
  );
}
